<template>
  <div
    class="rubrics-wrapper"
    :class="skeletonClass"
  >
    <section :class="rubricsClass">
      <div class="rubrics__header">
        <Text
          :text="title"
          :skeleton="isSkeletonActive"
          :skeleton-breakpoints="'104px'"
          class="rubrics__title"
        />

        <Text
          v-if="(isSkeletonActive || isShowRubric) && !isCityTagsCollectionsExperimentIsActive"
          :text="t('rubrics_section.more')"
          :skeleton="isSkeletonActive"
          :skeleton-breakpoints="'40px'"
          class="mobile-more-button"
          @click="handleOpenPopup"
        />
      </div>
      <div
        ref="tagsRubrics"
        :class="rubricsTagsCssClass"
      >
        <div class="rubrics__tags-desktop">
          <SkeletonBlock :is-active="isSkeletonActive">
            <template #skeleton>
              <div class="rubrics__tags-skeleton">
                <SkeletonItem
                  v-for="(width, id) of skeletonDesktopRow"
                  :key="id"
                  :width="width"
                  :height="32"
                />
              </div>
            </template>
            <UiTags
              v-if="isShowRubric"
              :key="rubricsBlockId"
              v-rubrics-view-tracker="soguRubricsViewData"
              :options="visibleTagOptions"
              :max-lines="rubricsMaxLines"
              @tag-click="handleVisibleTagClick"
            />
          </SkeletonBlock>
        </div>

        <div class="rubrics__tags-mobile">
          <SkeletonBlock :is-active="isSkeletonActive">
            <template #skeleton>
              <ExcursionRubricsSkeleton :widths="skeletonRows.firstRow" />

              <ExcursionRubricsSkeleton :widths="skeletonRows.secondRow" />
            </template>
            <UiTags
              v-if="isShowRubric"
              :key="topRubricsBlockId"
              v-rubrics-view-tracker="soguRubricsViewData"
              :options="topTagOptions"
              @tag-click="(index) => handleTagClick(index, topTags)"
            />
            <UiTags
              v-if="isShowRubric"
              :key="bottomRubricsBlockId"
              :options="bottomTagOptions"
              @tag-click="(index) => handleTagClick(index, bottomTags)"
            />
          </SkeletonBlock>
        </div>
        <RubricsPopup
          :key="uniqueKey"
          :open="isOpen"
          :title="title"
          :tags="unnulablePopupTagsMapped"
          :selected-rubric="selectedValue"
          :default-sogu-fields="defaultSoguFields"
          :filter-query="rubricTagQuery"
          @close-modal="handleCloseModal()"
          @select-rubric="onSelectRubric"
        />
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
  import { useI18n } from '@experience/i18n';
  import ru from '../../i18n/ru.json';
  import { useScreenSize } from '@experience/shared';
  import { type ListingTagMap, type ExtendedPartTagType } from '@/entities/rubrics';
  import RubricsPopup from '@/entities/rubrics/popup/ui/RubricsPopup.vue';
  import {
    RUBRICS_SKELETON_WIDTH_FIRST,
    RUBRICS_SKELETON_WIDTH_SECOND,
    useExcursionCityRubrics,
  } from '@/widgets/rubricsBlock/excursion';
  import { trackEvent, useViewTracker } from '@/entities/sogu';
  import { onMounted, onBeforeUnmount, ref, watch, computed } from 'vue';
  import { useRoute } from 'vue-router';
  import {
    type ExcursionParams,
    useExcursionExperienceAggregations,
  } from '@/entities/experience/aggregation/api/excursion/useExcursionExperienceAggregations.ts';
  import {
    buildExcursionAggregationTypeWithoutTours,
    excursionFiltersToQuery,
    useExcursionFiltersStore,
  } from '@/entities/filter/store/excursion';
  import { excursionBuildQueryFilterParams } from '@/entities/filter/store/utils/excursion/ExcursionBuildQueryFilterParams.ts';
  import { useSoguTags } from '@/entities/sogu/composables/useSoguTags.ts';
  import { Text } from '@/shared/ui/text';
  import { SkeletonBlock, SkeletonItem } from '@experience/core-skeleton-lib';
  import { useLoaderStore } from '@/shared/store/useLoaderStore';
  import ExcursionRubricsSkeleton from './ExcursionRubricsSkeleton.vue';
  import type { ContextGeoSubject } from '@/shared/types/geography.ts';
  import type { CityTag } from '@/shared/api/experience';
  import type { TExcursionFiltersPageState } from '@/entities/filter/store/types/excursion/state.ts';
  import { useExcursionSoguStore } from '@/features/sogu/excursion';
  import { useHeaderZIndex } from '@/shared/composables/useHeaderZIndex.ts';
  import { useSeoStore } from '@/shared/store/useSeoStore.ts';
  import { useExcursionListingSkeleton } from '@/shared/composables/excursion/useExcursionDelayedListingSkeleton.ts';
  import { useCityTagsCollectionsExperiment } from '@/features/experiments';
  import { UiTags } from '@experience/core-tag-ui';
  import { useSkeletonPrefetchStore } from '@/shared/store/useSkeletonPrefetchStore.ts';
  import { LocationType } from '@/entities/location';
  import { calcSkeletonWidths } from '../../lib/calcSkeletonWidths.ts';
  import { trimVisibleRubricsChips } from '../../lib/trimVisibleRubricsChips.ts';
  import { mapRubricsToTagOptions } from '../../lib/mapRubricsToTagOptions.ts';
  import { moreValue } from '../../lib/consts.ts';

  interface Props {
    slug: string;
    defaultSoguFields: ContextGeoSubject;
    cityTags?: CityTag[];
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{ tagChanged: [tag: ExtendedPartTagType] }>();

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  const { isExpActive } = useCityTagsCollectionsExperiment();
  const isCityTagsCollectionsExperimentIsActive = computed(() => {
    return isExpActive.value;
  });
  const rubricsCities = [
    'Saint_Petersburg',
    'Moscow',
    'Dubai',
    'Istanbul',
    'Murmansk',
    'Minsk',
    'Kaliningrad',
    'Sochi',
    'Phuket',
    'Kazan',
    'Tbilisi',
    'Sharm_El_Sheikh',
    'Kislovodsk',
    'Nizhny_Novgorod',
    'Yerevan',
  ];
  const rubricsMaxLines = computed(() => {
    if (!isCityTagsCollectionsExperimentIsActive.value) {
      return 0;
    }

    if (rubricsCities.includes(props.slug)) {
      return 3;
    }

    return 2;
  });

  const { isMobile } = useScreenSize();
  const loadersStore = useLoaderStore();
  const filtersStore = useExcursionFiltersStore();
  const seoStore = useSeoStore();
  const route = useRoute();

  const uniqueKey = ref(1);

  const { decreaseZIndex, increaseZIndex } = useHeaderZIndex();

  const isOpen = ref(false);
  const tagsRubrics = ref<HTMLElement | null>(null);

  const cityTagsRef = computed(() => props.cityTags || []);
  const soguStore = useExcursionSoguStore();

  const rubricsData = computed(() => {
    return useExcursionCityRubrics(cityTagsRef, isMobile);
  });

  const { isSkeletonActive } = useExcursionListingSkeleton();
  const { getTagsSkeleton, getExpCountByTagSkeleton } = useSkeletonPrefetchStore();

  const skeletonRows = computed(() => {
    const tags = getTagsSkeleton(LocationType.CITY);
    const expCountByTag = getExpCountByTagSkeleton();

    if (tags) {
      return calcSkeletonWidths(tags, expCountByTag);
    }

    return {
      firstRow: RUBRICS_SKELETON_WIDTH_FIRST,
      secondRow: RUBRICS_SKELETON_WIDTH_SECOND,
    };
  });

  const skeletonDesktopRow = computed(() => {
    return [...skeletonRows.value.firstRow, ...skeletonRows.value.secondRow].slice(0, 20);
  });

  const skeletonClass = computed(() => {
    return loadersStore.isFiltersLoading ? 'skeleton-active' : '';
  });
  const rubricsClass = computed(() => ['rubrics', 'wrap']);

  const { selectedValue, popupTagsMapped, visibleTags } = rubricsData.value;

  const currentTags = computed(() => {
    return props.cityTags;
  });

  watch(
    () => [currentTags.value, loadersStore.ready.rubrics],
    ([tags]) => {
      if (tags !== undefined) {
        if (!loadersStore.ready.rubrics) {
          loadersStore.ready.rubrics = true;
        }
        soguStore.rubricId = selectedValue.value.id || null;
      }
    },
    { immediate: true },
  );

  const aggregationParams = computed<ExcursionParams>((): ExcursionParams => {
    const pageParams: TExcursionFiltersPageState = {
      city: props.slug,
    };

    // Для агрегаций рубрик всегда добавляем tour, если не выбрана рубрика с форматом в городе
    const queryParams = excursionFiltersToQuery(filtersStore.searchState, pageParams, { forceAddTourToType: true });

    return {
      bookable: true,
      start_time_interval: 'day',
      duration_interval: 'upto_2_hours,from_6_hours,from_2_to_4_hours',
      ...queryParams,
      date_filter_mode: 'event_start',
      page: computed(() => 1),
    } as ExcursionParams;
  });

  // Отдельный запрос агрегаций для рубрики с не включенными турами
  // Приоритет: 1) выбранный формат из фильтров, 2) проверка на isTour
  const aggregationParamsForNotIncludedTours = computed<ExcursionParams>((): ExcursionParams => {
    const pageParams: TExcursionFiltersPageState = {
      city: props.slug,
    };

    const queryParams = excursionFiltersToQuery(filtersStore.searchState, pageParams);

    const typeForNotIncludedTours = buildExcursionAggregationTypeWithoutTours(filtersStore.searchState);

    return {
      bookable: true,
      start_time_interval: 'day',
      duration_interval: 'upto_2_hours,from_6_hours,from_2_to_4_hours',
      ...queryParams,
      type: typeForNotIncludedTours,
      date_filter_mode: 'event_start',
      page: computed(() => 1),
    } as ExcursionParams;
  });

  const { tags } = useExcursionExperienceAggregations(aggregationParams);
  const { tags: tagsForNotIncludedToursRubrics } = useExcursionExperienceAggregations(
    aggregationParamsForNotIncludedTours,
  );

  // Задаем counter с аггрегаций (при применении фильтров) и фильтрум по нулевым значениям
  const modifyTagsWithAggregation = (arr: ExtendedPartTagType[]) => {
    return arr
      .map((item) => {
        const id = item?.id || 0;

        // Для рубрик без включенных туров используем агрегацию без туров по её id
        // Туры подмешиваются только если is_tours_included === true
        if (item.is_tours_included !== true) {
          const notIncludedToursRubricId = item?.id || 0;
          const notIncludedToursRubricCounter = tagsForNotIncludedToursRubrics.value?.[notIncludedToursRubricId] || 0;

          return {
            ...item,
            counter: item.value !== 'more' ? notIncludedToursRubricCounter || 0 : undefined,
          };
        }

        // Для рубрик с включенными турами используем агрегацию с турами
        return { ...item, counter: item.value !== 'more' ? tags.value?.[id] || 0 : undefined };
      })
      .filter(
        (tag) =>
          !!tag?.counter || tag.value === 'more' || tag.value === (filtersStore.searchState.tag__slugs as string),
      );
  };

  const unnulableVisibleTags = computed(() => {
    let arr = modifyTagsWithAggregation(visibleTags.value);
    if (isMobile.value && isCityTagsCollectionsExperimentIsActive.value) {
      arr = rubricsCities.includes(props.slug) ? arr.slice(0, 16) : arr.slice(0, 10);
    }

    // Если рубрика с 0 экскурсий, и массив из 2 элементов (рубрика и кнопка "еще"), возвращаем только рубрику
    if (arr[0]?.counter === 0 && arr.length < 3 && !isMobile.value) {
      return [arr[0]];
    }

    if (!isCityTagsCollectionsExperimentIsActive.value && arr.length > 20) {
      return trimVisibleRubricsChips(arr, 20, filtersStore.searchState.tag__slugs || selectedValue.value.value);
    }

    return arr;
  });

  const selectedTagSlug = computed(() => selectedValue.value.value);

  const rubricTagQuery = computed(() => excursionBuildQueryFilterParams(filtersStore.searchState));

  const rubricsBlockId = computed(() => unnulableVisibleTags.value.reduce((a, v) => a + `${v.id}`, ''));

  const topTags = computed(() => {
    return unnulableVisibleTags.value.filter((_, index) => (index + 1) % 2 !== 0);
  });

  const topRubricsBlockId = computed(() => topTags.value.reduce((a, v) => a + `${v.id}`, ''));

  const bottomTags = computed(() => {
    return unnulableVisibleTags.value.filter((_, index) => (index + 1) % 2 === 0);
  });

  const bottomRubricsBlockId = computed(() => bottomTags.value.reduce((a, v) => a + `${v.id}`, ''));

  const visibleTagOptions = computed(() =>
    mapRubricsToTagOptions(unnulableVisibleTags.value, selectedTagSlug.value, rubricTagQuery.value),
  );

  const topTagOptions = computed(() =>
    mapRubricsToTagOptions(topTags.value, selectedTagSlug.value, rubricTagQuery.value),
  );

  const bottomTagOptions = computed(() =>
    mapRubricsToTagOptions(bottomTags.value, selectedTagSlug.value, rubricTagQuery.value),
  );

  const unnulablePopupTagsMapped = computed(() => {
    const arr = popupTagsMapped.value.map((group) => {
      return {
        ...group,
        tags: modifyTagsWithAggregation(group.tags),
      };
    }) as ListingTagMap[];

    return arr.filter((item) => Boolean(item.tags.length));
  });

  const isShowRubric = computed(() => {
    const firstTag = unnulableVisibleTags.value[0];

    return (
      Boolean(firstTag?.counter) ||
      (firstTag?.value === (filtersStore.searchState.tag__slugs as string) && unnulableVisibleTags.value.length > 1)
    );
  });

  const title = computed(() => {
    return isShowRubric.value ? t('rubrics_section.title') : '';
  });

  const rubricsTagsCssClass = computed(() => {
    let out = 'rubrics__tags';

    if (unnulableVisibleTags.value.length > 1 && !isCityTagsCollectionsExperimentIsActive.value) {
      out += ' rubrics__tags--multiple';
    }

    return out;
  });

  const handleOpenPopup = () => {
    decreaseZIndex();
    isOpen.value = true;
    trackEvent('listing:themes-popup.click', { ...props.defaultSoguFields.soguStamp });
    trackEvent('listing:themes-popup.view', { ...props.defaultSoguFields.soguStamp });
  };

  const handleTagClick = (index: number, rubricsTags: ExtendedPartTagType[], shouldSendSogu = true) => {
    const rubric = rubricsTags[index];

    if (!rubric) {
      return;
    }

    if (rubric.value === moreValue) {
      handleOpenPopup();

      return;
    }

    if (isMobile.value) {
      tagsRubrics.value?.scrollTo({
        left: 0,
        behavior: 'smooth',
      });
    }

    if (shouldSendSogu) {
      trackEvent('listing:category.click', {
        ...props.defaultSoguFields.soguStamp,
        tag_id: rubric.id,
        location: 'top',
      });
    }
  };

  const handleVisibleTagClick = (index: number) => {
    handleTagClick(index, unnulableVisibleTags.value);
  };

  const closeModal = () => {
    isOpen.value = false;
    increaseZIndex();
    setTimeout(() => {
      uniqueKey.value = uniqueKey.value + 1;
    });
  };

  const handleCloseModal = () => {
    trackEvent('listing:themes-popup.close', props.defaultSoguFields.soguStamp);
    closeModal();
  };

  const onSelectRubric = (rubric: ExtendedPartTagType) => {
    if (!rubric.url) {
      filtersStore.applyRubricSelection(rubric);
    }

    closeModal();
  };

  watch(
    () => [tagsRubrics.value, loadersStore.isFiltersLoading],
    ([newTagsRubrics, newIsFiltersLoading]) => {
      if (newTagsRubrics && !newIsFiltersLoading) {
        observeRubrics();
      }
    },
    { immediate: true }, // Для немедленного запуска после монтирования
  );

  const observer = ref<IntersectionObserver | null>(null);

  const observeRubrics = () => {
    if (!tagsRubrics.value) {
      return;
    }
    // с nextTick не успевает отрендерить чипсы
    setTimeout(() => {
      const tagElements = tagsRubrics.value?.querySelectorAll('.ui-tag') || [];
      observer.value = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const targetElement = entry.target as HTMLElement;
              const id = +targetElement.id || undefined;
              const isActive = entry.target.classList.contains('ui-tag--selected');

              trackEvent('listing:exact-category.view', {
                ...props.defaultSoguFields.soguStamp,
                is_active: isActive,
                tag_id: id,
              });
            }
          });
        },
        { root: null, threshold: 0.5 },
      );

      tagElements.forEach((tag) => observer.value?.observe(tag));
    }, 1000);
  };

  watch(
    () => route.path,
    () => {
      observer.value?.disconnect();
      observeRubrics();
    },
  );

  // const resultsCount = computed(() => filtersStore.searchTotalCount || 0);

  // const showRubricBlock = computed(() => Boolean(resultsCount.value));

  const setFocusOnActiveTag = () => {
    const activeTag = tagsRubrics.value?.querySelector('.ui-tag--selected');
    if (activeTag && 'focus' in activeTag && typeof activeTag.focus === 'function') {
      activeTag.focus();
    }
  };

  const { soguTags } = useSoguTags(computed(() => visibleTags.value));

  const soguRubricsViewData = () => ({
    ...props.defaultSoguFields.soguStamp,
    tag_ids: soguTags.value,
    location: 'top',
  });

  const vRubricsViewTracker = useViewTracker('listing:category.view');

  onMounted(observeRubrics);
  onBeforeUnmount(() => {
    observer.value?.disconnect();
  });

  watch(
    () => selectedValue.value,
    (tag) => {
      emit('tagChanged', tag);
      seoStore.setTagCanonicalUrl(tag?.seoUrl || '');
      setFocusOnActiveTag();
    },
  );
</script>

<style scoped lang="scss">
  @use '@experience/design-tokens/typography' as typography;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .rubrics__tags-skeleton {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .rubrics-wrapper {
    @include adaptivity.mobile {
      overflow: hidden;
    }
  }

  .rubrics {
    margin-top: 32px;

    &.rubrics--tour {
      @include adaptivity.mobile {
        margin-top: 60px;
      }
    }

    @include adaptivity.mobile {
      padding: 0;
    }

    @include adaptivity.tablet {
      margin-top: 16px;
    }
  }

  .rubrics__header {
    @include adaptivity.mobile {
      display: flex;
      justify-content: space-between;
      margin-bottom: 16px;
      padding: 0 20px;
    }

    @include adaptivity.mf-tablet {
      margin-bottom: 8px;
    }
  }

  .rubrics__title {
    @include typography.base-700-500-extra;

    @include adaptivity.tablet {
      @include typography.base-600-500-extra;
    }

    @include adaptivity.mobile {
      @include typography.base-400-400-semi;
    }
  }

  .mobile-more-button {
    display: none;

    @include adaptivity.mobile {
      display: block;
      color: var(--alias-color-link-default);

      @include typography.base-400-400-medium;
    }
  }

  .rubrics__tags {
    margin-top: 16px;
    -ms-overflow-style: none;
    scrollbar-width: none;
    scroll-behavior: smooth;

    :deep(.ui-tag) {
      flex-shrink: 0;
    }

    :deep(.ui-tag__label) {
      white-space: nowrap;
    }

    :deep(.ui-tag--selected) {
      .ui-tag__label {
        color: var(--comp-tag-color-label-selected);
      }

      .ui-tag__counter {
        color: var(--comp-tag-color-counter-selected);
      }
    }

    &::-webkit-scrollbar {
      display: none;
    }

    @include adaptivity.mobile {
      display: flex;
      overflow: scroll hidden;
      margin-top: 0;
      padding-right: 20px;
      padding-left: 20px;

      :deep(.ui-tags) {
        flex-wrap: nowrap;
      }
    }
  }

  .rubrics__tags--multiple {
    :deep(.ui-tag:last-child) {
      background: none;
      outline: 1px solid var(--comp-button-outline-color-border-neutral);
      outline-offset: -1px;

      &:hover {
        outline: 1px solid var(--comp-button-outline-color-border-neutral-hovered);
      }

      &:active {
        outline: 1px solid var(--comp-button-outline-color-border-neutral-pressed);
      }

      &:focus-visible {
        outline: 1px solid var(--comp-tag-color-border-focus);
      }

      .ui-tag__label {
        color: var(--comp-tag-color-label);
      }
    }

    @include adaptivity.mobile {
      :deep(.ui-tag:last-child) {
        background: var(--comp-tag-color-background);
        outline: none;
      }
    }
  }

  .rubrics__tags-desktop {
    @include adaptivity.mobile {
      display: none;
    }
  }

  .rubrics__tags-mobile {
    display: none;

    @include adaptivity.mobile {
      display: flex;
      flex-direction: column;
      row-gap: 8px;
    }
  }

  .hide-more-btn {
    :deep(.ui-tag:last-child) {
      display: none;
    }
  }
</style>
